"use client";

import { c as _c } from "react/compiler-runtime";
import { type InfiniteData, type UseInfiniteQueryResult, useInfiniteQuery } from "@tanstack/react-query";
import { parseAsInteger, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import { isLoadingBoundaries } from "../db/get-index";
import { filterQueryOptions } from "../lib/react-query";
import type { FilterResultPage, FilterSearchParams } from "../types/filter";
import { useClient } from "../utils/client/client";
import { normalizeFilterSearchParams } from "../utils/filterSearch/utils";
import { useGeoSearch } from "./use-geosearch";
import { useLocale } from "./use-locale";
import { useSearchState } from "./use-search-state";
export function useFilterSearch() {
  const $ = _c(21);
  const locale = useLocale();
  const client = useClient();
  const [isMounted, setIsMounted] = useState(false);
  const {
    geoLocation,
    boundaries,
    radius
  } = useGeoSearch();
  const {
    filterQuery,
    getQueryState,
    searchQuery
  } = useSearchState();
  const [pageSizeParam] = useQueryState("pageSize", parseAsInteger);
  const hasActiveFilters = Object.values(filterQuery).some(_temp);
  const hasSearchQuery = !!searchQuery;
  const shouldKeepPreviousData = hasActiveFilters || hasSearchQuery;
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== boundaries || $[3] !== geoLocation || $[4] !== radius) {
    t2 = isLoadingBoundaries(geoLocation, radius, boundaries);
    $[2] = boundaries;
    $[3] = geoLocation;
    $[4] = radius;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isLoading = t2;
  let t3;
  let t4;
  if ($[6] !== boundaries || $[7] !== client || $[8] !== geoLocation || $[9] !== getQueryState || $[10] !== isLoading || $[11] !== isMounted || $[12] !== locale || $[13] !== pageSizeParam || $[14] !== radius || $[15] !== searchQuery || $[16] !== shouldKeepPreviousData) {
    const filter = getQueryState();
    const filterSearchParams = normalizeFilterSearchParams({
      searchQuery: searchQuery || "",
      filter,
      geoLocation,
      boundaries,
      radius,
      subclient: client,
      pageSize: pageSizeParam ?? undefined
    });
    t3 = useInfiniteQuery;
    t4 = filterQueryOptions.search(locale, filterSearchParams, {
      enabled: isMounted && !isLoading,
      refetch: true,
      placeholderData: shouldKeepPreviousData ? _temp2 : undefined
    });
    $[6] = boundaries;
    $[7] = client;
    $[8] = geoLocation;
    $[9] = getQueryState;
    $[10] = isLoading;
    $[11] = isMounted;
    $[12] = locale;
    $[13] = pageSizeParam;
    $[14] = radius;
    $[15] = searchQuery;
    $[16] = shouldKeepPreviousData;
    $[17] = t3;
    $[18] = t4;
  } else {
    t3 = $[17];
    t4 = $[18];
  }
  let results = t3(t4);
  if (isLoading) {
    let t5;
    if ($[19] !== results) {
      t5 = {
        ...results,
        isFetching: true,
        isLoading: true,
        isFetchingNextPage: false,
        isFetched: false
      };
      $[19] = results;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    results = t5 as UseInfiniteQueryResult<InfiniteData<FilterResultPage, unknown>, Error>;
  }
  return results;
}
function _temp2(previousData) {
  return previousData;
}
function _temp(v) {
  return v && v.length > 1;
}